import { useEffect, useRef, useState } from 'react';
import { fetchMaterialById, fetchMaterialSignedUrl } from '../../../features/materials/api';
import { detectIsTemporary } from '../../../features/materials/materialUrl';

/** URLとして有効か（httpまたは/で始まるか） */
const isValidUrl = (s: string | undefined | null): s is string =>
  !!s && (s.startsWith('http') || s.startsWith('/'));

/**
 * サウンドロゴ音声プレーヤー。
 *
 * サウンドロゴ素材は一時フォルダにも共有フォルダにも存在しうるため、
 * NarrationPlayer の temporary 専用解決ではなく、素材の所在に応じて
 * temporary / cloud の署名付きURLエンドポイントを使い分ける。
 */
export function SoundLogoPlayer({ materialId }: { materialId?: string | null }) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!materialId) {
      setAudioUrl(null);
      setIsPlaying(false);
      return;
    }
    let cancelled = false;
    setAudioUrl(null);
    setIsPlaying(false);
    (async () => {
      try {
        const material = await fetchMaterialById(materialId);
        if (cancelled || !material) return;
        // 1. 素材レスポンスに有効なURLがあればそのまま使う
        const direct = [
          material.preview_url,
          (material as { source_url?: string }).source_url,
          (material as { public_url?: string }).public_url,
        ].find(isValidUrl);
        if (direct) {
          setAudioUrl(direct);
          return;
        }
        // 2. 所在 (temporary / cloud) に応じた署名付きURLを取得
        const signed = await fetchMaterialSignedUrl(materialId, detectIsTemporary(material));
        if (!cancelled && isValidUrl(signed)) setAudioUrl(signed);
      } catch {
        // 解決失敗時はURLなし（再生ボタンは無効状態のまま）
      }
    })();
    return () => { cancelled = true; };
  }, [materialId]);

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const hasAudio = !!materialId && !!audioUrl;

  return (
    <>
      {audioUrl && (
        <audio
          ref={audioRef}
          key={materialId}
          src={audioUrl}
          onEnded={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          preload="metadata"
          style={{ display: 'none' }}
        />
      )}
      <button
        type="button"
        onClick={togglePlay}
        disabled={!hasAudio}
        title={isPlaying ? '停止' : 'サウンドロゴを再生'}
        style={{
          border: 'none',
          background: 'transparent',
          cursor: hasAudio ? 'pointer' : 'default',
          padding: '0',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <img
          src={isPlaying ? '/button_stop_purple.svg' : '/button_play_purple.svg'}
          alt={isPlaying ? '停止' : '再生'}
          style={{ width: '24px', height: '24px', ...(hasAudio ? {} : { filter: 'grayscale(100%) opacity(0.4)' }) }}
        />
      </button>
    </>
  );
}
